import React, { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { CheckCircle, X, ArrowRight, CircleNotch } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import TigerLogo from "@/components/TigerLogo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function BillingSuccess() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState({ payment_status: "pending" });
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) return;
    let stopped = false;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/billing/status/${sessionId}`);
        if (stopped) return;
        setStatus(data);
        if (data.payment_status !== "paid" && attempts < 15) {
          setTimeout(() => setAttempts((a) => a + 1), 2000);
        }
      } catch (e) {
        if (!stopped && attempts < 15) setTimeout(() => setAttempts((a) => a + 1), 2000);
      }
    };
    poll();
    return () => { stopped = true; };
    // eslint-disable-next-line
  }, [sessionId, attempts]);

  const paid = status?.payment_status === "paid";

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-50 to-orange-100 p-6">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
        <TigerLogo size={72} className="mx-auto mb-4" />
        {paid ? (
          <>
            <CheckCircle size={64} weight="fill" className="text-emerald-500 mx-auto mb-3" />
            <h1 className="font-heading text-2xl font-bold mb-2" data-testid="billing-success-title">
              Abbonamento attivato
            </h1>
            <p className="text-slate-600 mb-1">Grazie, il tuo accesso al modulo formativo è attivo.</p>
            {status.plan_name && (
              <p className="text-sm text-slate-500 mb-6">Piano: <b>{status.plan_name}</b></p>
            )}
            <div className="text-xs text-slate-500 mb-6">
              Ti abbiamo inviato la ricevuta via email.
            </div>
            <Button
              data-testid="go-to-app"
              className="w-full min-h-[48px] bg-slate-900 hover:bg-slate-800"
              onClick={() => nav("/triage")}
            >
              Entra nel modulo formativo <ArrowRight size={18} className="ml-2" />
            </Button>
          </>
        ) : (
          <>
            <CircleNotch size={48} className="text-slate-400 mx-auto mb-3 animate-spin" />
            <h1 className="font-heading text-xl font-semibold mb-2">Confermiamo il pagamento…</h1>
            <p className="text-sm text-slate-500 mb-4">
              Questa pagina si aggiornerà da sola non appena Stripe conferma la transazione.
            </p>
            <div className="text-xs text-slate-400">Tentativo {attempts + 1}/15</div>
          </>
        )}
      </div>
    </div>
  );
}

export function BillingCancel() {
  const nav = useNavigate();
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
        <TigerLogo size={64} className="mx-auto mb-4" />
        <X size={48} weight="bold" className="text-slate-400 mx-auto mb-3" />
        <h1 className="font-heading text-xl font-semibold mb-2" data-testid="billing-cancel-title">
          Pagamento annullato
        </h1>
        <p className="text-slate-600 mb-6">Non è stato addebitato nulla. Puoi riprovare quando vuoi.</p>
        <Button data-testid="back-to-billing" className="w-full min-h-[48px]" onClick={() => nav("/billing")}>
          Torna ai piani
        </Button>
      </div>
    </div>
  );
}
